import {
  ChakraFlex,
  ChakraVStack,
  DateFormat,
  SectionCard,
  SkeletonLoader,
  Text,
  Tag,
} from '@equip.health/ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { getDateTagProps } from '~/lib/util/date.util';
import { getDiffInWeeks } from '~/lib/util/patient.util';

interface TreatmentEpisodeDetailsSectionCardProps {
  treatmentEpisode?: TreatmentEpisode;
  isLoading?: boolean;
  insuranceAuthorizationEndDate?: string;
}

interface DetailRowProps {
  id: string;
  label: string;
  value: string;
  valueColor?: string;
  secondaryText?: string;
  tagId?: string;
  tagProps?: ReturnType<typeof getDateTagProps>;
}

const DetailRow = ({
  id,
  label,
  value,
  valueColor,
  secondaryText,
  tagId,
  tagProps,
}: DetailRowProps) => {
  return (
    <ChakraFlex
      alignItems="center"
      gap="6px"
      alignSelf="stretch"
      role="group"
      aria-labelledby={id}
    >
      <Text
        color="neutral.secondary"
        id={id}
        variant="caption"
        width="250px"
        role="term"
      >
        {label}
      </Text>
      <Text color={valueColor} role="definition">{value}</Text>
      {secondaryText && (
        <Text color="neutral.secondary" role="definition">{secondaryText}</Text>
      )}
      {tagProps && tagId && (
        <Tag
          id={tagId}
          color={tagProps.color}
          value={tagProps.value}
          maxWidth="100%"
          aria-label={tagProps.value}
        />
      )}
    </ChakraFlex>
  );
};

export const TreatmentEpisodeDetailsSectionCard = ({
  treatmentEpisode,
  isLoading,
  insuranceAuthorizationEndDate,
}: TreatmentEpisodeDetailsSectionCardProps) => {
  const {
    displayInsuranceAuthEndingDates: isDisplayInsuranceAuthEndingDatesEnabled,
    enableDisplayInsurancePart1: isEnableDisplayInsurancePart1,
  } = useFlags<LDFlags>();

  const isInsuranceAuthEndingDatesActive =
    isDisplayInsuranceAuthEndingDatesEnabled &&
    isEnableDisplayInsurancePart1;
  if (isLoading) {
    return (
      <SkeletonLoader
        height="75px"
        id="treatment-episode-skeleton"
        width="100%"
      />
    );
  }

  const admitDateRaw = treatmentEpisode?.admitDate;
  const closedDateRaw = treatmentEpisode?.closedDate;
  const insuranceAuthorizationEndDateRaw = insuranceAuthorizationEndDate;

  const formattedAdmitDate = admitDateRaw
    ? DateTime.fromFormat(admitDateRaw, DateFormat.shortYearMonthDay).toFormat(
        DateFormat.longDate,
      )
    : '';

  const weeksAtEquip = admitDateRaw
    ? String(
        Math.max(
          1,
          getDiffInWeeks(
            admitDateRaw,
            closedDateRaw ?? new Date().toISOString().slice(0, 10),
            DateFormat.shortYearMonthDay,
          ),
        ),
      )
    : '1';

  const estimatedProgramEndDateRaw = admitDateRaw
    ? DateTime.fromFormat(admitDateRaw, DateFormat.shortYearMonthDay)
        .plus({ weeks: 52 })
        .toFormat(DateFormat.shortYearMonthDay)
    : undefined;

  const formattedEstimatedProgramEndDate = estimatedProgramEndDateRaw
    ? DateTime.fromFormat(estimatedProgramEndDateRaw, DateFormat.shortYearMonthDay)
        .toFormat(DateFormat.longDate)
    : 'Not set';

  const formattedInsuranceAuthorizationEndDate = insuranceAuthorizationEndDateRaw
    ? DateTime.fromFormat(
        insuranceAuthorizationEndDateRaw,
        DateFormat.shortYearMonthDay,
      ).toFormat(DateFormat.longDate)
    : 'None on file';

  const estimatedProgramEndTagProps = getDateTagProps(estimatedProgramEndDateRaw);
  const insuranceAuthorizationEndTagProps = getDateTagProps(
    insuranceAuthorizationEndDateRaw,
  );

  return (
    <SectionCard id="treatment-episodes-details">
      <ChakraVStack
        justifyContent="space-between"
        alignItems="flex-start"
        gap="12px"
        width="100%"
      >
        <DetailRow
          id="admit-date-label"
          label="Admit date"
          value={formattedAdmitDate}
        />
        <DetailRow
          id="weeks-at-equip-label"
          label="Weeks at Equip"
          value={weeksAtEquip}
        />
        {isEnableDisplayInsurancePart1 && (
            <DetailRow
              id="estimated-program-end-label"
              label="Estimated program end"
              value={formattedEstimatedProgramEndDate}
              secondaryText="(Week 52)"
              tagId="estimated-program-end-tag"
              tagProps={estimatedProgramEndTagProps}
            />
          )}
          {isInsuranceAuthEndingDatesActive && (
            <DetailRow
              id="insurance-auth-end-label"
              label="Insurance auth end"
              value={formattedInsuranceAuthorizationEndDate}
              valueColor={
                insuranceAuthorizationEndDateRaw ? undefined : 'neutral.tertiary'
              }
              tagId="insurance-auth-end-tag"
              tagProps={insuranceAuthorizationEndTagProps}
            />
        )}
      </ChakraVStack>
    </SectionCard>
  );
};
